import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Spinner } from '@fluentui/react-components';
import { ProtectedRoute } from '@shared/components/ProtectedRoute';
import { APPROVING_ROLE, PLATFORM_ADMIN_ROLE } from '@shared/auth/roles';
import { MainLayout } from '@shared/components/layouts/MainLayout';
import { AdminLayout } from '@shared/components/layouts/AdminLayout';
import { KioskLayout } from '@shared/components/layouts/KioskLayout';
import { LoginLayout } from '@shared/components/layouts/LoginLayout';
import { ErrorBoundary } from '@shared/components/ErrorBoundary';
import { TenantCacheBridgeOutlet } from '@app/TenantCacheBridge';

// Lazy-loaded pages
const HomePage = lazy(() => import('@features/dashboard/components/HomePage'));
const LoginPage = lazy(() => import('@features/auth/components/LoginPage'));
const LogoutPage = lazy(() => import('@features/auth/components/LogoutPage'));
const LogoutCallback = lazy(() => import('@features/auth/components/LogoutCallback'));
const AccessDenied = lazy(() => import('@features/auth/components/AccessDenied'));
const NotFoundPage = lazy(() => import('@features/auth/components/NotFoundPage'));

// Employee pages
const EmployeeList = lazy(() => import('@features/employees/components/EmployeeList'));

// Visitor pages
const VisitorList = lazy(() => import('@features/visitors/components/VisitorList'));
const AppointmentList = lazy(() => import('@features/visitors/components/AppointmentList'));
const VisitorRegistrationPage = lazy(() => import('@features/visitor-registration/components/VisitorRegistrationPage'));

// Reception pages
const ReceptionDashboard = lazy(() => import('@features/reception/components/ReceptionDashboard'));
const ContractorReceptionDashboard = lazy(() => import('@features/reception/components/ContractorReceptionDashboard'));

// Files pages
const FilesPage = lazy(() => import('@features/files/components/FilesPage'));
const DocumentsPage = lazy(() => import('@features/documents/components/DocumentsPage'));

// Notifications (panel in Header, no dedicated page needed)

// Profile pages
const ProfileSettingsPage = lazy(() => import('@features/auth/components/ProfileSettingsPage'));

// Admin pages
const UserList = lazy(() => import('@features/admin/users/components/UserList'));
const TenantList = lazy(() => import('@features/admin/tenants/components/TenantList'));
const TenantSettingsPage = lazy(() => import('@features/admin/tenants/components/TenantSettingsPage'));
const WorkflowList = lazy(() => import('@features/admin/workflows/components/WorkflowList'));
const WorkflowBracketCanvasEditor = lazy(() => import('@features/admin/workflows/canvas/WorkflowBracketCanvasEditor'));
const WorkflowInstanceList = lazy(() => import('@features/admin/workflow-instances/components/WorkflowInstanceList'));
const WorkflowInstanceDetail = lazy(() => import('@features/admin/workflow-instances/components/WorkflowInstanceDetail'));
const MailTemplateList = lazy(() => import('@features/admin/templates/components/MailTemplateList'));
const MailTemplateEditor = lazy(() => import('@features/admin/templates/components/MailTemplateEditor'));
const FormDefinitionList = lazy(() => import('@features/admin/forms/components/FormDefinitionList'));
const FormDefinitionEditor = lazy(() => import('@features/admin/forms/components/FormDefinitionEditor'));
const DocumentsAdminPage = lazy(() => import('@features/documents/components/DocumentsAdminPage'));
const AppearanceSettings = lazy(() => import('@features/admin/components/AppearanceSettings'));
const KioskList = lazy(() => import('@features/admin/kiosks/components/KioskList'));

// Admin pages - Batch 1 Migration
const AccessControlMapping = lazy(() => import('@features/admin/access-control/components/AccessControlMapping'));
const AnalyticsDashboard = lazy(() => import('@features/admin/analytics/components/AnalyticsDashboard'));
const AdminRoomList = lazy(() => import('@features/admin/rooms/components/RoomList'));
const RoomCalendarPage = lazy(() => import('@features/admin/rooms/components/RoomCalendarPage'));
const AdminVisitorTypeList = lazy(() => import('@features/admin/visitor-types/components/VisitorTypeList'));
const SafetyTrainingList = lazy(() => import('@features/admin/safety-training/components/SafetyTrainingList'));
const VisitorContractList = lazy(() => import('@features/admin/visitors/components/VisitorContractList'));
const HealthCheckList = lazy(() => import('@features/admin/health/components/HealthCheckList'));
const BanlistPage = lazy(() => import('@features/admin/banlist/components/BanlistPage'));
// Phase 47 plan 47-02: retention-eligibility admin list (RET-01, D-11)
const DataRetentionList = lazy(() => import('@features/admin/data-retention/components/DataRetentionList'));
const TransconnectRequestLogList = lazy(() => import('@features/admin/transconnect/components/TransconnectRequestLogList'));

// Wachbuch admin pages (Phase 18 plan 18-16)
const LogbookAdminPage = lazy(() => import('@features/admin/logbook/components/LogbookAdminPage'));
const MatrixEditPage = lazy(() => import('@features/admin/logbook/components/MatrixEditPage'));

// Issuance pages
const IssuanceItemList = lazy(() => import('@features/issuance/components/ItemList'));
const IssuancePersonList = lazy(() => import('@features/issuance/components/PersonList'));
const KeyIssuancePage = lazy(() => import('@features/issuance/components/KeyIssuancePage'));
const KeyAssignmentsPage = lazy(() => import('@features/issuance/components/KeyAssignmentsPage'));

// Kiosk pages
const KioskIndex = lazy(() => import('@features/kiosk/components/KioskIndex'));
const KioskSafetyTraining = lazy(() => import('@features/kiosk/components/KioskSafetyTraining'));
const KioskDsgvo = lazy(() => import('@features/kiosk/components/KioskDsgvo'));
const KioskSignature = lazy(() => import('@features/kiosk/components/KioskSignature'));
const KioskRegister = lazy(() => import('@features/kiosk/components/KioskRegister'));
// Phase 34 plan 34-01: kiosk entry/scan/dispatch screens
const KioskEntryTiles = lazy(() => import('@features/kiosk/components/KioskEntryTiles'));
const KioskCheckInScan = lazy(() => import('@features/kiosk/components/KioskCheckInScan'));
const KioskWaitingForInput = lazy(() => import('@features/kiosk/components/KioskWaitingForInput'));
// Phase 35 plan 35-12: the journey's end — approval wait and completion confirmation
const KioskWaitingForApproval = lazy(() => import('@features/kiosk/components/KioskWaitingForApproval'));
const KioskConfirmation = lazy(() => import('@features/kiosk/components/KioskConfirmation'));

// Search page
const SearchPage = lazy(() => import('@features/search/components/SearchPage'));

// Wachbuch porter pages (Phase 18 plan 18-17 — Variante D porter UX)
const IncidentList = lazy(() => import('@features/incidents/components/IncidentList'));
const MeldungenPage = lazy(() => import('@features/logbook/components/MeldungenPage'));
const ExportPage = lazy(() => import('@features/logbook/components/ExportPage'));

// Contractors pages
const ContractorList = lazy(() => import('@features/contractors/components/ContractorList'));

// External Employees pages
const ExternalEmployeesPage = lazy(() => import('@features/external-employees/components/ExternalEmployeesPage'));

// Contacts pages
const ContactList = lazy(() => import('@features/contacts/components/ContactList'));

// Approvals pages (Phase 25 plan 25-10; role-gated by Phase 43 D-11/D-12) — deliberately
// registered as top-level children of the roles-free <ProtectedRoute><MainLayout/></ProtectedRoute>
// block below, NOT nested under /admin/*. Originally D-03 (SPEC §14) kept this route guard-free
// because eligibility was a dynamic per-stage check, re-verified live server-side, never a static
// role — see backlog item 999.12 and 25-CONTEXT.md. Phase 43 replaces that with a real Keycloak
// role (`admin` or `Approving`, plans 43-01/43-05), so the route object below now carries its own
// ProtectedRoute matching the server's RequireRole(admin, Approving) gate.
const ApprovalInbox = lazy(() => import('@features/approvals/components/ApprovalInbox'));
const ApprovalDetail = lazy(() => import('@features/approvals/components/ApprovalDetail'));

// Outlook pages live in a separate Vite entry (outlook.html → src/outlook/main.tsx)
// because Office.js patches window.history.replaceState in OWA, which crashes
// BrowserRouter at module init. The Outlook task pane uses a HashRouter; this
// router (the main Maja app) does not handle /outlook routes anymore.

// Loading fallback
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spinner size="large" label="Loading..." />
  </div>
);

const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

/**
 * The `/admin/workflows` subtree, exported so `__tests__/workflowBracketRoutes.test.tsx` can mount
 * the REAL route objects over a memory router and prove the redirect below actually resolves —
 * rather than re-declaring a look-alike tree that could drift from this one.
 */
export const workflowBracketRoutes: RouteObject[] = [
  {
    index: true,
    element: <SuspenseWrapper><WorkflowList /></SuspenseWrapper>,
  },
  {
    path: 'new',
    element: <Navigate to="/admin/workflows" replace />,
  },
  {
    // Phase 40 plan 40-11 Task 1 (checkpoint decision: option-a) — the Zeitstrahl timeline editor
    // is retired; the canvas is the only bracket editor. This path REDIRECTS rather than 404s, so
    // an admin's bookmarked timeline link still lands on a working editor for the same bracket.
    // `to="canvas"` is resolved relative to THIS route, i.e. /admin/workflows/:id/canvas.
    path: ':id',
    element: <Navigate to="canvas" replace />,
  },
  {
    path: ':id/canvas',
    element: <SuspenseWrapper><WorkflowBracketCanvasEditor /></SuspenseWrapper>,
  },
];

/**
 * The `/admin/workflow-instances` route pair, exported so
 * `__tests__/workflowInstanceRoutes.test.tsx` can mount the REAL route objects rather than a
 * look-alike tree that could drift from this one. Phase 41 plan 41-07, D-08: the operator instance
 * list opens to `gate_keeper` (the porter), not just `admin` — see the sibling route object below
 * for why this array is mounted OUTSIDE the `/admin/*` subtree.
 */
export const workflowInstanceRoutes: RouteObject[] = [
  {
    index: true,
    element: <SuspenseWrapper><WorkflowInstanceList /></SuspenseWrapper>,
  },
  {
    path: ':id',
    element: <SuspenseWrapper><WorkflowInstanceDetail /></SuspenseWrapper>,
  },
];

/**
 * The tenant settings routes of the `/admin` subtree (Phase 51 plan 51-10, D-11), exported so
 * `__tests__/tenantSettingsRoutes.test.tsx` can mount the REAL route objects. `tenants/:tab` renders
 * Mandanteneinstellungen (general | locations | receptions); an unknown tab — including the old
 * `tenants/new` and `tenants/<id>` bookmarks — is redirected by the page itself. The former
 * standalone Standorte / Empfänge URLs redirect to their tabs.
 */
export const tenantSettingsRoutes: RouteObject[] = [
  {
    path: 'tenants',
    children: [
      {
        index: true,
        element: <SuspenseWrapper><TenantSettingsPage /></SuspenseWrapper>,
      },
      {
        path: ':tab',
        element: <SuspenseWrapper><TenantSettingsPage /></SuspenseWrapper>,
      },
    ],
  },
  {
    // D-10: the cross-tenant list + create. Cosmetic guard — the server enforces platform_admin (T-51-49).
    path: 'all-tenants',
    element: (
      <ProtectedRoute roles={[PLATFORM_ADMIN_ROLE]}>
        <SuspenseWrapper><TenantList /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: 'locations',
    element: <Navigate to="/admin/tenants/locations" replace />,
  },
  {
    path: 'receptions',
    element: <Navigate to="/admin/tenants/receptions" replace />,
  },
];

export const router = createBrowserRouter([
  {
    element: <TenantCacheBridgeOutlet />,
    errorElement: <ErrorBoundary />,
    children: [
      // Public routes (Login Layout)
      {
        element: <LoginLayout />,
        children: [
          {
            path: '/login',
            element: <SuspenseWrapper><LoginPage /></SuspenseWrapper>,
          },
          {
            path: '/logout',
            element: <SuspenseWrapper><LogoutPage /></SuspenseWrapper>,
          },
          {
            path: '/logout-callback',
            element: <SuspenseWrapper><LogoutCallback /></SuspenseWrapper>,
          },
          {
            path: '/access-denied',
            element: <SuspenseWrapper><AccessDenied /></SuspenseWrapper>,
          },
        ],
      },

      // Protected routes (Main Layout)
      {
        element: (
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <SuspenseWrapper><HomePage /></SuspenseWrapper>,
          },
          {
            path: 'dashboard',
            element: <SuspenseWrapper><HomePage /></SuspenseWrapper>,
          },
          {
            // Visitor Registration Wizard
            path: 'visitor-registration/:id?',
            element: <SuspenseWrapper><VisitorRegistrationPage /></SuspenseWrapper>,
          },
          {
            // External Employees (contractor employees with tokens)
            path: 'external-employees',
            element: <SuspenseWrapper><ExternalEmployeesPage /></SuspenseWrapper>,
          },
          {
            path: 'employees',
            children: [
              {
                index: true,
                element: <SuspenseWrapper><EmployeeList /></SuspenseWrapper>,
              },
            ],
          },
          {
            path: 'visitors',
            children: [
              {
                index: true,
                element: <SuspenseWrapper><VisitorList /></SuspenseWrapper>,
              },
              {
                path: 'appointments/:appointmentId?',
                element: <SuspenseWrapper><AppointmentList /></SuspenseWrapper>,
              },
            ],
          },
          {
            path: 'reception/:appointmentId?',
            element: (
              <ProtectedRoute roles={['admin', 'gate_keeper']}>
                <SuspenseWrapper><ReceptionDashboard /></SuspenseWrapper>
              </ProtectedRoute>
            ),
          },
          {
            path: 'reception-contractor',
            element: (
              <ProtectedRoute roles={['admin', 'gate_keeper']}>
                <SuspenseWrapper><ContractorReceptionDashboard /></SuspenseWrapper>
              </ProtectedRoute>
            ),
          },
          {
            path: 'files',
            element: <SuspenseWrapper><FilesPage /></SuspenseWrapper>,
          },
          {
            path: 'documents',
            element: <SuspenseWrapper><DocumentsPage /></SuspenseWrapper>,
          },
          {
            // Notifications are shown as a panel from the Header; keep route for backwards compat
            path: 'notifications',
            element: <SuspenseWrapper><HomePage /></SuspenseWrapper>,
          },
          {
            path: 'profile',
            element: <SuspenseWrapper><ProfileSettingsPage /></SuspenseWrapper>,
          },
          {
            path: 'issuance',
            children: [
              {
                path: 'items',
                element: (
                  <ProtectedRoute roles={['admin', 'gate_keeper']}>
                    <SuspenseWrapper><IssuanceItemList /></SuspenseWrapper>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'persons',
                element: (
                  <ProtectedRoute roles={['admin', 'gate_keeper']}>
                    <SuspenseWrapper><IssuancePersonList /></SuspenseWrapper>
                  </ProtectedRoute>
                ),
              },
                {
                    path: 'history',
                    element: (
                        <ProtectedRoute roles={['admin', 'gate_keeper']}>
                            <SuspenseWrapper><KeyAssignmentsPage /></SuspenseWrapper>
                        </ProtectedRoute>
                    ),
                },
              {
                path: 'key-issuance',
                element: (
                  <ProtectedRoute roles={['admin', 'gate_keeper']}>
                    <SuspenseWrapper><KeyIssuancePage /></SuspenseWrapper>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'key-assignments',
                element: (
                  <ProtectedRoute roles={['admin', 'gate_keeper']}>
                    <SuspenseWrapper><KeyAssignmentsPage /></SuspenseWrapper>
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: 'search',
            element: (
              <ProtectedRoute roles={['admin']}>
                <SuspenseWrapper><SearchPage /></SuspenseWrapper>
              </ProtectedRoute>
            ),
          },
          {
            // Phase 18 plan 18-17: Wachbuch porter UX (Vorfall + Meldungen + Export tabs)
            path: 'logbook',
            element: (
              <ProtectedRoute roles={['admin', 'employee', 'gate_keeper']}>
                <Outlet />
              </ProtectedRoute>
            ),
            children: [
              { index: true, element: <Navigate to="/logbook/incident" replace /> },
              { path: 'incident', element: <SuspenseWrapper><IncidentList /></SuspenseWrapper> },
              { path: 'reports', element: <SuspenseWrapper><MeldungenPage /></SuspenseWrapper> },
              { path: 'export', element: <SuspenseWrapper><ExportPage /></SuspenseWrapper> },
            ],
          },
          {
            // Legacy redirect — preserve the old URL for bookmarks / SignalR notifications shipped before 18-15.
            path: 'incidents',
            element: <Navigate to="/logbook/incident" replace />,
          },
          {
            path: 'contractors',
            element: (
              <ProtectedRoute roles={['admin', 'contractor_manager']}>
                <Outlet />
              </ProtectedRoute>
            ),
            children: [
              {
                index: true,
                element: <SuspenseWrapper><ContractorList /></SuspenseWrapper>,
              },
              {
                // Redirect old routes to main list (sidebar handles create/edit)
                path: 'new',
                element: <Navigate to="/contractors" replace />,
              },
              {
                path: ':id',
                element: <Navigate to="/contractors" replace />,
              },
            ],
          },
          {
            path: 'contacts',
            children: [
              {
                index: true,
                element: <SuspenseWrapper><ContactList /></SuspenseWrapper>,
              },
              {
                // Redirect old routes to main list (sidebar handles create/edit)
                path: 'new',
                element: <Navigate to="/contacts" replace />,
              },
              {
                path: ':id',
                element: <Navigate to="/contacts" replace />,
              },
            ],
          },
          {
            // Approver inbox + detail (Phase 25 plan 25-10; role-gated by Phase 43 D-11/D-12).
            // Originally sat inside the roles-free ProtectedRoute above with no guard of its own,
            // because D-03 made eligibility a dynamic per-stage check re-verified server-side,
            // never a static role. Phase 43 replaces that with a real Keycloak role (`admin` or
            // `Approving`); this ProtectedRoute is cosmetic — the server's
            // RequireRole(admin, Approving) on every approval-workflow route is the actual
            // enforcement — but it keeps a non-approver from even loading the page.
            path: 'approvals',
            element: (
              <ProtectedRoute roles={['admin', APPROVING_ROLE]}>
                <Outlet />
              </ProtectedRoute>
            ),
            children: [
              {
                index: true,
                element: <SuspenseWrapper><ApprovalInbox /></SuspenseWrapper>,
              },
              {
                path: ':instanceId',
                element: <SuspenseWrapper><ApprovalDetail /></SuspenseWrapper>,
              },
            ],
          },
          {
            // Phase 41 plan 41-07, D-08: this is a SIBLING of the `admin` route object below, not a
            // child of it — a nested `roles={['admin', 'gate_keeper']}` guard inside the admin
            // subtree would never render for a porter, because the outer admin
            // `<ProtectedRoute roles={['admin']}>` redirects to /access-denied before any child
            // route is evaluated. Using a multi-segment literal `path` here preserves the
            // `/admin/workflow-instances` URL (an existing admin bookmark keeps working) while
            // escaping the admin-only gate. The pages therefore render inside `MainLayout` rather
            // than `AdminLayout` — the same trade `reception`/`reception-contractor` above already
            // make — so an admin sees the main chrome on these two pages instead of the admin
            // sidebar. That is a deliberate, accepted visual change, not a regression.
            path: 'admin/workflow-instances',
            element: (
              <ProtectedRoute roles={['admin', 'gate_keeper']}>
                <Outlet />
              </ProtectedRoute>
            ),
            children: workflowInstanceRoutes,
          },
        ],
      },

      // Admin routes (Admin Layout)
      {
        path: 'admin',
        element: (
          <ProtectedRoute roles={['admin']}>
            <AdminLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            // Default admin page is Analytics Dashboard (exakt wie Blazor @page "/admin")
            index: true,
            element: <SuspenseWrapper><AnalyticsDashboard /></SuspenseWrapper>,
          },
          {
            path: 'users',
            children: [
              {
                index: true,
                element: <SuspenseWrapper><UserList /></SuspenseWrapper>,
              },
              {
                // Redirect old routes to main list (sidebar handles permissions)
                path: ':id/permissions',
                element: <Navigate to="/admin/users" replace />,
              },
            ],
          },
          ...tenantSettingsRoutes,
          {
            path: 'workflows',
            children: workflowBracketRoutes,
          },
          {
            path: 'documents',
            children: [
              {
                index: true,
                element: <SuspenseWrapper><DocumentsAdminPage /></SuspenseWrapper>,
              },
            ],
          },
          {
            // Route: /admin/mail-templates - matches Blazor @page "/admin/mail-templates"
            path: 'mail-templates',
            children: [
              {
                index: true,
                element: <SuspenseWrapper><MailTemplateList /></SuspenseWrapper>,
              },
              {
                // Route: /admin/mail-templates/create - matches Blazor @page "/admin/mail-templates/create"
                path: 'create',
                element: <SuspenseWrapper><MailTemplateEditor /></SuspenseWrapper>,
              },
              {
                // Route: /admin/mail-templates/:id/edit - matches Blazor @page "/admin/mail-templates/{Id:guid}/edit"
                path: ':id/edit',
                element: <SuspenseWrapper><MailTemplateEditor /></SuspenseWrapper>,
              },
            ],
          },
          {
            // Redirect old route for backwards compatibility
            path: 'templates',
            element: <Navigate to="/admin/mail-templates" replace />,
          },
          {
            // Route: /admin/form-definitions - matches Blazor @page "/admin/form-definitions"
            path: 'form-definitions',
            children: [
              {
                index: true,
                element: <SuspenseWrapper><FormDefinitionList /></SuspenseWrapper>,
              },
              {
                path: 'new',
                element: <SuspenseWrapper><FormDefinitionEditor /></SuspenseWrapper>,
              },
              {
                path: ':id',
                element: <SuspenseWrapper><FormDefinitionEditor /></SuspenseWrapper>,
              },
              {
                path: ':id/edit',
                element: <SuspenseWrapper><FormDefinitionEditor /></SuspenseWrapper>,
              },
            ],
          },
          {
            // Redirect old route for backwards compatibility
            path: 'forms',
            element: <Navigate to="/admin/form-definitions" replace />,
          },
          {
            path: 'settings',
            children: [
              {
                // Redirect /admin/settings to /admin/settings/appearance
                index: true,
                element: <Navigate to="/admin/settings/appearance" replace />,
              },
              {
                path: 'appearance',
                element: <SuspenseWrapper><AppearanceSettings /></SuspenseWrapper>,
              },
            ],
          },
          {
            // Route: /admin/kiosk - matches Blazor @page "/admin/kiosk" (singular)
            path: 'kiosk',
            element: <SuspenseWrapper><KioskList /></SuspenseWrapper>,
          },
          {
            // Redirect old route for backwards compatibility
            path: 'kiosks',
            element: <Navigate to="/admin/kiosk" replace />,
          },
          {
            // Route: /admin/issuance - matches Blazor issuance routes
            path: 'issuance',
            children: [
              {
                // Route: /admin/issuance/items - matches Blazor @page "/admin/issuance/items"
                path: 'items',
                element: <SuspenseWrapper><IssuanceItemList /></SuspenseWrapper>,
              },
              {
                // Route: /admin/issuance/persons - matches Blazor @page "/admin/issuance/persons"
                path: 'persons',
                element: <SuspenseWrapper><IssuancePersonList /></SuspenseWrapper>,
              },
            ],
          },
          // Batch 1 Migration - New Admin Routes
          {
            path: 'accessControl',
            element: <SuspenseWrapper><AccessControlMapping /></SuspenseWrapper>,
          },
          {
            // Analytics Dashboard - default admin page (exakt wie Blazor @page "/admin")
            path: 'analytics',
            element: <SuspenseWrapper><AnalyticsDashboard /></SuspenseWrapper>,
          },
          {
            path: 'rooms',
            children: [
              {
                index: true,
                element: <SuspenseWrapper><AdminRoomList /></SuspenseWrapper>,
              },
              {
                path: ':id/calendar',
                element: <SuspenseWrapper><RoomCalendarPage /></SuspenseWrapper>,
              },
            ],
          },
          {
            // Phase 20 plan 20-06: admin VisitorTypes — flat route, in-page Drawer, mirrors Locations
            path: 'visitor-types',
            element: <SuspenseWrapper><AdminVisitorTypeList /></SuspenseWrapper>,
          },
          {
            path: 'safetyTrainings',
            element: <SuspenseWrapper><SafetyTrainingList /></SuspenseWrapper>,
          },
          {
            path: 'banlist',
            element: <SuspenseWrapper><BanlistPage /></SuspenseWrapper>,
          },
          {
            // Phase 47 plan 47-02: retention-eligibility admin list (RET-01, D-11)
            path: 'data-retention',
            element: <SuspenseWrapper><DataRetentionList /></SuspenseWrapper>,
          },
          {
            // Quick 261015-bw9: Transconnect request log (admin only via the parent ProtectedRoute)
            path: 'transconnect',
            element: <SuspenseWrapper><TransconnectRequestLogList /></SuspenseWrapper>,
          },
          {
            path: 'visitors',
            element: <SuspenseWrapper><VisitorContractList /></SuspenseWrapper>,
          },
          {
            // Route: /admin/health - Service health check dashboard
            path: 'health',
            element: <SuspenseWrapper><HealthCheckList /></SuspenseWrapper>,
          },
          {
            // Phase 18 plan 18-16: Wachbuch (Variante D matrix admin)
            path: 'logbook',
            children: [
              {
                index: true,
                element: <SuspenseWrapper><LogbookAdminPage /></SuspenseWrapper>,
              },
              {
                path: ':matrixId',
                element: <SuspenseWrapper><MatrixEditPage /></SuspenseWrapper>,
              },
            ],
          },
        ],
      },

      // Kiosk routes (Kiosk Layout) - publicly accessible, no auth required
      {
        path: 'kiosk',
        element: <KioskLayout />,
        children: [
          {
            index: true,
            element: <SuspenseWrapper><KioskIndex /></SuspenseWrapper>,
          },
          {
            path: 'entry',
            element: <SuspenseWrapper><KioskEntryTiles /></SuspenseWrapper>,
          },
          {
            path: 'scan',
            element: <SuspenseWrapper><KioskCheckInScan /></SuspenseWrapper>,
          },
          {
            path: 'step',
            element: <SuspenseWrapper><KioskWaitingForInput /></SuspenseWrapper>,
          },
          {
            path: 'waiting-approval',
            element: <SuspenseWrapper><KioskWaitingForApproval /></SuspenseWrapper>,
          },
          {
            path: 'confirmation',
            element: <SuspenseWrapper><KioskConfirmation /></SuspenseWrapper>,
          },
          {
            path: 'checkin/safety-training',
            element: <SuspenseWrapper><KioskSafetyTraining /></SuspenseWrapper>,
          },
          {
            path: 'checkin/dsgvo',
            element: <SuspenseWrapper><KioskDsgvo /></SuspenseWrapper>,
          },
          {
            path: 'checkin/signature',
            element: <SuspenseWrapper><KioskSignature /></SuspenseWrapper>,
          },
          {
            path: 'register',
            element: <SuspenseWrapper><KioskRegister /></SuspenseWrapper>,
          },
        ],
      },

      // Outlook task-pane routes have moved to outlook.html (separate Vite
      // entry, HashRouter) — see src/outlook/OutlookRouter.tsx. They are not
      // part of the BrowserRouter mounted from index.html anymore.

      // 404
      {
        path: '*',
        element: <SuspenseWrapper><NotFoundPage /></SuspenseWrapper>,
      },
    ],
  },
]);
